import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Download, KeyRound, LogIn, Mail, MessageSquare, MonitorSmartphone, Printer, QrCode, Trash2, UserCheck, UserX, Users, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { errMsg } from "@/lib/api";

const day = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }) : "—");
const STATE_STYLE = { active: "bg-brand-pale text-brand", used: "bg-gray-100", expired: "bg-danger-pale text-danger", revoked: "bg-danger-pale text-danger" };

export async function openOrderScreen(api, venue, name) {
  const r = await api.post(`/venues/${venue.id}/counter-session`, { display_name: name || "" });
  localStorage.setItem(`lg_staff_${r.data.slug}`, r.data.token);
  window.location.assign(`/staff/${r.data.slug}`);
}

export const OrderScreenButton = ({ api, venue, className = "btn-primary", testid = "open-order-screen-quick-btn", children }) => (
  <button className={className} onClick={() => openOrderScreen(api, venue).catch((e) => toast.error(errMsg(e)))} data-testid={testid}>
    <MonitorSmartphone size={16} /> {children || "Open order screen"}
  </button>
);

const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const printCard = (venue, url, img) => {
  const w = window.open("", "_blank");
  const name = esc(venue.name);
  if (!w) { toast.error("Allow pop-ups to print, or use Download QR instead"); return; }
  w.document.write(`<html><head><title>Staff sign-in · ${name}</title></head><body style="font-family:Arial,sans-serif;text-align:center;padding:40px">
    <img src="/brand/logo-horizontal.png" style="height:40px" alt="LineGone"/><h1 style="margin:24px 0 4px">Staff sign-in</h1>
    <p style="font-size:20px;margin:0 0 24px">${name}</p><img src="${img}" style="width:320px;height:320px" alt="QR code"/>
    <p style="font-size:16px;margin-top:20px">Scan to open the order screen, then sign in with your own email or phone and password or PIN.</p>
    <p style="font-size:14px;color:#555">${esc(url)}</p><p style="font-size:12px;color:#777">Staff only — customers order from the LineGone QR sign.</p>
    <script>window.onload=()=>setTimeout(()=>window.print(),300)</script></body></html>`);
  w.document.close();
};

const StaffLinkCard = ({ venue }) => {
  const url = `${window.location.origin}/staff/${venue.slug}`;
  const box = useRef(null);
  const img = () => box.current?.querySelector("canvas")?.toDataURL("image/png");
  const copy = () => { navigator.clipboard?.writeText(url); toast.success("Staff sign-in link copied"); };
  const download = () => { const a = document.createElement("a"); a.href = img(); a.download = `${venue.slug}-staff-sign-in-qr.png`; a.click(); };
  return (
    <div className="card-lg p-5" data-testid="staff-link-card">
      <h3 className="flex items-center gap-2 text-lg font-extrabold"><LogIn size={18} /> Staff sign-in link</h3>
      <p className="mt-1 text-sm text-muted2">Staff open this to see and handle orders. Stick the QR by the counter. Staff only — it's not your customer QR.</p>
      <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div ref={box} className="w-fit rounded-xl border border-line bg-white p-2" data-testid="staff-signin-qr"><QRCodeCanvas value={url} size={512} marginSize={2} fgColor="#1F2A2E" style={{ width: 140, height: 140 }} /></div>
        <div className="min-w-0">
          <a href={url} target="_blank" rel="noreferrer" className="link-lg block break-all text-sm" data-testid="staff-signin-url">{url}</a>
          <div className="mt-3 flex flex-wrap gap-2">
            <button className="btn-primary !py-2 text-sm" onClick={copy} data-testid="copy-staff-signin-link-btn"><Copy size={14} /> Copy staff sign-in link</button>
            <button className="btn-secondary !py-2 text-sm" onClick={download} data-testid="download-staff-qr-btn"><Download size={14} /> Download QR</button>
            <button className="btn-secondary !py-2 text-sm" onClick={() => printCard(venue, url, img())} data-testid="print-staff-qr-btn"><Printer size={14} /> Print counter card</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CounterCard = ({ api, venue, name }) => {
  const [n, setN] = useState(name);
  useEffect(() => { setN(name); }, [name]);
  const open = () => openOrderScreen(api, venue, n).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="card-lg p-5" data-testid="counter-card">
      <h3 className="flex items-center gap-2 text-lg font-extrabold"><MonitorSmartphone size={18} /> Work the counter yourself</h3>
      <p className="mt-1 text-sm text-muted2">Opens the staff order screen signed in as you. Customers see this first name on ready orders.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Input className="max-w-[200px]" value={n} onChange={(e) => setN(e.target.value)} data-testid="owner-display-name-input" />
        <button className="btn-primary !py-2" disabled={!n.trim()} onClick={open} data-testid="open-order-screen-btn">Open order screen</button>
      </div>
    </div>
  );
};

const InviteRow = ({ i, onRevoke, api, venue }) => {
  const link = `${window.location.origin}/staff/join?code=${i.code}`;
  const [qr, setQr] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState("");
  const box = useRef(null);
  const copy = () => { navigator.clipboard?.writeText(link); toast.success("Join link copied"); };
  const smsBody = `You're invited to join ${venue.name} on LineGone. Create your staff account here: ${link} (invite code ${i.code}, works once, expires in 7 days)`;
  const sendEmail = async () => {
    try {
      const r = await api.post(`/venues/${venue.id}/invites/${i.code}/email`, { email, origin_url: window.location.origin });
      if (r.data.sent) toast.success(`Invite emailed to ${email}`); else toast.warning("Invite saved, but email sending isn't set up yet. Send it by text or copy the link instead.");
      setEmailOpen(false); setEmail("");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const download = () => { const a = document.createElement("a"); a.href = box.current.querySelector("canvas").toDataURL("image/png"); a.download = `linegone-staff-invite-${i.code}.png`; a.click(); };
  return (
    <li className="py-3" data-testid={`invite-row-${i.code}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <span className="font-mono text-lg font-black tracking-widest" data-testid="invite-code">{i.code}</span>
        <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-bold ${STATE_STYLE[i.state]}`} data-testid={`invite-state-${i.code}`}>{i.state}</span>
        <div className="text-xs text-muted2">{i.note && <b>{i.note} · </b>}{i.state === "active" ? `Expires ${day(i.expires_at)}` : i.state === "used" ? `Used ${day(i.used_at)}` : ""}</div>
      </div>
      {i.state === "active" && <div className="flex flex-wrap gap-2">
        <a className="btn-secondary !px-3 !py-1.5 text-sm" href={`sms:?&body=${encodeURIComponent(smsBody)}`} data-testid={`text-invite-${i.code}`}><MessageSquare size={14} /> Text invite</a>
        <button className="btn-secondary !px-3 !py-1.5 text-sm" onClick={() => setEmailOpen(!emailOpen)} data-testid={`email-invite-${i.code}`}><Mail size={14} /> Email invite</button>
        <button className="btn-secondary !px-3 !py-1.5 text-sm" onClick={() => setQr(!qr)} data-testid={`show-invite-qr-${i.code}`}><QrCode size={14} /> {qr ? "Hide QR" : "Show QR"}</button>
        <button className="btn-secondary !px-3 !py-1.5 text-sm" onClick={copy} data-testid={`copy-invite-${i.code}`}><Copy size={14} /> Copy link</button>
        <button className="flex items-center gap-1 rounded-full border-2 border-line px-3 py-1.5 text-sm font-bold text-danger" onClick={() => onRevoke(i)} data-testid={`revoke-invite-${i.code}`}><XCircle size={14} /> Cancel</button>
      </div>}
    </div>
    {emailOpen && i.state === "active" && (
      <div className="mt-3 flex flex-wrap gap-2" data-testid={`email-invite-form-${i.code}`}>
        <Input type="email" className="max-w-[260px]" placeholder="staff@email.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={`email-invite-input-${i.code}`} />
        <button className="btn-primary !py-2 text-sm" disabled={!email.includes("@")} onClick={sendEmail} data-testid={`email-invite-send-${i.code}`}>Send invite</button>
      </div>
    )}
    {qr && i.state === "active" && (
      <div className="mt-3 flex flex-wrap items-center gap-4 rounded-2xl bg-brand-pale p-3" data-testid={`invite-qr-${i.code}`}>
        <div ref={box} className="rounded-xl bg-white p-2"><QRCodeCanvas value={link} size={512} marginSize={2} fgColor="#1F2A2E" style={{ width: 150, height: 150 }} /></div>
        <div className="text-sm">
          <p className="font-semibold">Have the new staff member scan this with their phone camera.</p>
          <p className="text-muted2">It opens the join page with code {i.code} filled in. One use only.</p>
          <button className="btn-secondary mt-2 !py-1.5 text-sm" onClick={download} data-testid={`download-invite-qr-${i.code}`}><Download size={14} /> Download QR</button>
        </div>
      </div>
    )}
    </li>
  );
};

const StaffRow = ({ s, onActive, onRemove }) => (
  <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`staff-row-${s.id}`}>
    <div className="min-w-0">
      <span className="font-bold" data-testid="staff-row-name">{s.display_name}</span>
      <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-bold ${s.active ? "bg-brand-pale text-brand" : "bg-danger-pale text-danger"}`} data-testid={`staff-status-${s.id}`}>{s.active ? "active" : "deactivated"}</span>
      <div className="text-xs text-muted2">{s.email || s.phone} · last sign-in {day(s.last_login_at)}</div>
    </div>
    <div className="flex gap-2">
      <button className="btn-secondary !px-3 !py-1.5 text-sm" onClick={() => onActive(s, !s.active)} data-testid={`toggle-staff-${s.id}`}>
        {s.active ? <><UserX size={14} /> Deactivate</> : <><UserCheck size={14} /> Reactivate</>}</button>
      <button className="rounded-full border-2 border-line px-3 text-danger" onClick={() => onRemove(s)} aria-label="Remove" data-testid={`remove-staff-${s.id}`}><Trash2 size={14} /></button>
    </div>
  </li>
);

export default function StaffPanel({ api, venue }) {
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const load = useCallback(() => api.get(`/venues/${venue.id}/staff`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [api, venue.id]);
  useEffect(() => { load(); }, [load]);
  const run = async (p, msg) => { try { await p; if (msg) toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  const invite = () => run(api.post(`/venues/${venue.id}/invites`, { note }).then(() => setNote("")), "Invite code created — valid for 7 days, one use");
  const revoke = (i) => run(api.delete(`/venues/${venue.id}/invites/${i.code}`), "Invite cancelled");
  const setActive = (s, active) => run(api.put(`/venues/${venue.id}/staff/${s.id}/active`, { active }), active ? `${s.display_name} reactivated` : `${s.display_name} deactivated and signed out`);
  const remove = (s) => window.confirm(`Remove ${s.display_name}? They are signed out at once.`) && run(api.delete(`/venues/${venue.id}/staff/${s.id}`), `${s.display_name} removed`);
  if (!data) return <p className="mt-4 text-muted2">Loading staff…</p>;
  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-2" data-testid="staff-panel">
      <div className="grid content-start gap-4">
        <CounterCard api={api} venue={venue} name={data.owner_display_name} />
        <StaffLinkCard venue={venue} />
        <div className="card-lg p-5" data-testid="invites-card">
          <h3 className="flex items-center gap-2 text-lg font-extrabold"><KeyRound size={18} /> Invite staff</h3>
          <p className="mt-1 text-sm text-muted2">One code per person. Single use, expires after 7 days. Staff register at <b>/staff/join</b>.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Input className="max-w-[220px]" placeholder="Who is it for? (optional)" value={note} maxLength={40} onChange={(e) => setNote(e.target.value)} data-testid="invite-note-input" />
            <button className="btn-primary !py-2" onClick={invite} data-testid="create-invite-btn">Create invite code</button>
          </div>
          <ul className="mt-2 divide-y divide-line">{data.invites.map((i) => <InviteRow key={i.code} i={i} onRevoke={revoke} api={api} venue={venue} />)}</ul>
        </div>
      </div>
      <div className="card-lg content-start p-5" data-testid="staff-list-card">
        <h3 className="flex items-center gap-2 text-lg font-extrabold"><Users size={18} /> Your staff ({data.staff.length})</h3>
        <ul className="mt-2 divide-y divide-line">{data.staff.map((s) => <StaffRow key={s.id} s={s} onActive={setActive} onRemove={remove} />)}</ul>
        {data.staff.length === 0 && <p className="mt-3 text-sm text-muted2">No staff yet. Create an invite code to add your first team member.</p>}
      </div>
    </div>
  );
}
